<template>
  <span>
    <v-row dense justify="center" v-for="chart in chartDataComputed" :key="chart.title">
      <v-col cols="12">
        <v-card-charts-dashboard
          :title="chart.title"
          :options="chart.options"
          :deep-copy-on-update="false"
          :is-loading="chart.loading"
        />
      </v-col>
    </v-row>
  </span>
</template>

<script>
import Highcharts from "highcharts";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
HighchartsNoData(Highcharts);
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import { mapState } from "vuex";
import DashboardReligacaoService from "@/services/DashboardReligacao";
import dayjs from "dayjs";

exporting(Highcharts);
exportData(Highcharts);
Highcharts.setOptions({
  lang: {
    decimalPoint: ",",
    thousandsSep: ".",
  },
});

export default {
  name: "DashboardReligacaoVisaoMensal",
  components: {
    VCardChartsDashboard: () =>
      import("@/components/base/VCardChartsDashboard.vue"),
  },
  props: {
    activatedTab: {
      type: Number,
      required: true,
    },
    visaoGraficos: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dadosIncidenciaSuspensoesIndevidas: {},
      dadosQntdTotalSuspensoesIndevidas: {},
      dadosPercentualServicosReligacao: {},
      dadosQntdServicosDescumpridos: {},
      dadosQntdServicosRealizados: {},
      dadosIncidenciaSuspensoesIndevidasAcumulados: {},
      dadosQtndTotalSuspensoesIndevidasAcumulados: {},
      dadosPercentualServicosReligacaoAcumulados: {},
      dadosQntdServicosDescumpridosAcumulados: {},
      dadosQntdServicosRealizadosAcumulados: {},
      graficoIncidenciaOptions: {},
      graficoQntdTotalOptions: {},
      graficoPercentualOptions: {},
      graficoQntdDescumpridosOptions: {},
      graficoQntdServicosRealizadosOptions: {},
      debounceTimer: null,
      isLoadingDados: false,
      loadingPorChave: {
        dadosMensais: false,
        dadosAcumulados: false,
      },
      chartData: [
        {
          title: "Incidência de Suspensões Indevidas",
          chartOptionsKey: "graficoIncidenciaOptions",
        },
        {
          title: "Quantidade Total de Suspensões Indevidas",
          chartOptionsKey: "graficoQntdTotalOptions",
        },
        {
          title: "Percentual de Serviços de Religação concluídos Fora do Prazo (%)",
          chartOptionsKey: "graficoPercentualOptions",
        },
        {
          title: "Quantidade de Serviços com Descumprimento de Prazo",
          chartOptionsKey: "graficoQntdDescumpridosOptions",
        },
        {
          title: "Quantidade de Serviços Realizados",
          chartOptionsKey: "graficoQntdServicosRealizadosOptions",
        },
      ],
    };
  },
  computed: {
    ...mapState("parametrosDashboardReligacao", [
      "competencia",
      "regionalSelecionada",
      "quantidadeMunicipios",
      "servicosReligacaoSelecionados",
    ]),
    chartDataComputed() {
      const loadingKey =
        this.visaoGraficos === "acumulados" ? "dadosAcumulados" : "dadosMensais";
      const isLoading = !!this.loadingPorChave[loadingKey];
      return this.chartData.map((chart) => ({
        ...chart,
        loading: isLoading,
        options: this[chart.chartOptionsKey],
      }));
    },
  },
  watch: {
    competencia() {
      this.getDadosComDebounce();
    },
    regionalSelecionada() {
      this.getDadosComDebounce();
    },
    servicosReligacaoSelecionados() {
      this.getDadosComDebounce();
    },
    visaoGraficos(newVal) {
      if (this.activatedTab === 0) {
        this.getDadosComDebounce();
      }
    },
  },
  mounted() {
    this.getDados();
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.limparDados();
  },
  methods: {
    processarDadosParaGrafico(dados) {
      const anoAtual = dayjs(this.competencia).year();
      const anoAnterior = anoAtual - 1;

      const meses = [
        "01",
        "02",
        "03",
        "04",
        "05",
        "06",
        "07",
        "08",
        "09",
        "10",
        "11",
        "12",
      ];
      const dadosAnoAtual = [];
      const dadosAnoAnterior = [];

      meses.forEach((mes) => {
        const chaveAtual = `${mes}/${anoAtual}`;
        const chaveAnterior = `${mes}/${anoAnterior}`;

        dadosAnoAtual.push(this.formatarValorParaGrafico(dados[chaveAtual]));
        dadosAnoAnterior.push(
          this.formatarValorParaGrafico(dados[chaveAnterior])
        );
      });

      return {
        categorias: [
          "Jan",
          "Fev",
          "Mar",
          "Abr",
          "Mai",
          "Jun",
          "Jul",
          "Ago",
          "Set",
          "Out",
          "Nov",
          "Dez",
        ],
        series: [
          {
            name: String(anoAnterior),
            data: dadosAnoAnterior,
            color: this.$vuetify.theme.themes.light.primary,
          },
          {
            name: String(anoAtual),
            data: dadosAnoAtual,
            color: this.$vuetify.theme.themes.light.accent,
          },
        ],
      };
    },

    criarOpcoesGrafico(titulo, dados, sufixo = "", valorReferencia = null) {
      const isPercentual = sufixo === "%";
      const dadosNormalizados = this.normalizarDados(dados, isPercentual);
      const referenciaNormalizada = this.normalizarValorReferencia(
        valorReferencia,
        isPercentual
      );
      const { categorias, series } = this.processarDadosParaGrafico(
        dadosNormalizados
      );

      return {
        chart: {
          type: "column",
          zoomType: "x",
          animation: {
            duration: 800,
          },
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: categorias,
          crosshair: true,
        },
        yAxis: {
          min: 0,
          title: {
            text: titulo,
          },
          labels: {
            formatter() {
              const valorFormatado = Highcharts.numberFormat(this.value, 2);
              return isPercentual
                ? `${valorFormatado}${sufixo}`
                : valorFormatado;
            },
          },
          plotLines: referenciaNormalizada
            ? [
                {
                  value: referenciaNormalizada,
                  width: 2,
                  zIndex: 5,
                  label: {
                    text: `Limite de Referência (${Highcharts.numberFormat(
                      referenciaNormalizada,
                      2
                    )}${sufixo})`,
                    align: "right",
                    verticalAlign: "top",
                    style: {
                      color: this.$vuetify.theme.themes.light.success,
                      fontSize: "14px",
                      fontWeight: "bold",
                    },
                  },
                },
              ]
            : [],
        },
        tooltip: {
          shared: true,
          valueSuffix: sufixo,
          valueDecimals: 2,
        },
        plotOptions: {
          column: {
            pointPadding: 0.2,
            borderWidth: 0,
            dataLabels: {
              enabled: false,
            },
          },
        },
        series,
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: [
                "downloadPNG",
                "downloadJPEG",
                "downloadPDF",
                "downloadSVG",
              ],
            },
          },
        },
      };
    },

    renderizarGraficos() {
      if (this.visaoGraficos === "acumulados") {
        this.graficoIncidenciaOptions = this.criarOpcoesGrafico(
          "Incidência",
          this.dadosIncidenciaSuspensoesIndevidasAcumulados,
          "",
          3
        );
        this.graficoQntdTotalOptions = this.criarOpcoesGrafico(
          "Quantidade",
          this.dadosQtndTotalSuspensoesIndevidasAcumulados
        );
        this.graficoPercentualOptions = this.criarOpcoesGrafico(
          "Percentual",
          this.dadosPercentualServicosReligacaoAcumulados,
          "%",
          5
        );
        this.graficoQntdDescumpridosOptions = this.criarOpcoesGrafico(
          "Quantidade",
          this.dadosQntdServicosDescumpridosAcumulados
        );
        this.graficoQntdServicosRealizadosOptions = this.criarOpcoesGrafico(
          "Quantidade",
          this.dadosQntdServicosRealizadosAcumulados
        );
        return;
      }
      this.graficoIncidenciaOptions = this.criarOpcoesGrafico(
        "Incidência",
        this.dadosIncidenciaSuspensoesIndevidas,
        "",
        3
      );
      this.graficoQntdTotalOptions = this.criarOpcoesGrafico(
        "Quantidade",
        this.dadosQntdTotalSuspensoesIndevidas,
        "",
        5
      );
      this.graficoPercentualOptions = this.criarOpcoesGrafico(
        "Percentual",
        this.dadosPercentualServicosReligacao,
        "%"
      );
      this.graficoQntdDescumpridosOptions = this.criarOpcoesGrafico(
        "Quantidade",
        this.dadosQntdServicosDescumpridos
      );
      this.graficoQntdServicosRealizadosOptions = this.criarOpcoesGrafico(
        "Quantidade",
        this.dadosQntdServicosRealizados
      );
    },

    getDadosMensais() {
      return Promise.all([
        this.getIncidenciaSuspensoesIndevidasVisaoMensal(),
        this.getQntdTotalSuspensoesIndevidasVisaoMensal(),
        this.getPercentualServicosReligacaoVisaoMensal(),
        this.getQntdServicosDescumpridosVisaoMensal(),
        this.getQntdServicosRealizadosVisaoMensal(),
      ])
        .then(() => {
          this.renderizarGraficos();
        })
        .catch((err) => {
          throw err;
        });
    },
    getDadosAcumulados() {
      return Promise.all([
        this.getIncidenciaSuspensoesIndevidasVisaoMensalAcumulados(),
        this.getQtndTotalSuspensoesIndevidasVisaoMensalAcumulados(),
        this.getPercentualServicosReligacaoVisaoMensalAcumulados(),
        this.getQntdServicosDescumpridosVisaoMensalAcumulados(),
        this.getQntdServicosRealizadosVisaoMensalAcumuladas(),
      ])
        .then(() => {
          this.renderizarGraficos();
        })
        .catch((err) => {
          throw err;
        });
    },
    getDados() {
      if (this.activatedTab !== 0 || this.isLoadingDados) return;

      this.isLoadingDados = true;
      this.loadingPorChave.dadosMensais = this.visaoGraficos !== "acumulados";
      this.loadingPorChave.dadosAcumulados = this.visaoGraficos === "acumulados";
      this.$emit("updateLoadingDados", true);

      this.$nextTick().then(() => {
        this.limparDados();

        const promise =
          this.visaoGraficos === "acumulados"
            ? this.getDadosAcumulados()
            : this.getDadosMensais();

        promise
          .catch((err) => {
            console.error(err);
            this.$toast.error("Erro ao recuperar dados", "", {
              position: "topRight",
            });
          })
          .finally(() => {
            this.isLoadingDados = false;
            this.loadingPorChave.dadosMensais = false;
            this.loadingPorChave.dadosAcumulados = false;
            this.$emit("updateLoadingDados", false);
          });
      });
    },

    getDadosComDebounce() {
      if (this.activatedTab !== 0) return;

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      this.loadingPorChave.dadosMensais = this.visaoGraficos !== "acumulados";
      this.loadingPorChave.dadosAcumulados = this.visaoGraficos === "acumulados";
      this.$emit("updateLoadingDados", true);

      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        this.getDados();
      }, 500);
    },

    limparDados() {
      this.dadosIncidenciaSuspensoesIndevidas = {};
      this.dadosQntdTotalSuspensoesIndevidas = {};
      this.dadosPercentualServicosReligacao = {};
      this.dadosQntdServicosDescumpridos = {};
      this.dadosQntdServicosRealizados = {};
      this.dadosIncidenciaSuspensoesIndevidasAcumulados = {};
      this.dadosQtndTotalSuspensoesIndevidasAcumulados = {};
      this.dadosPercentualServicosReligacaoAcumulados = {};
      this.dadosQntdServicosDescumpridosAcumulados = {};
      this.dadosQntdServicosRealizadosAcumulados = {};
      this.graficoIncidenciaOptions = {};
      this.graficoQntdTotalOptions = {};
      this.graficoPercentualOptions = {};
      this.graficoQntdDescumpridosOptions = {};
      this.graficoQntdServicosRealizadosOptions = {};
    },
    normalizarDados(dados, isPercentual) {
      const origem = dados || {};

      return Object.keys(origem).reduce((resultado, chave) => {
        const valor = Number(origem[chave]);
        const valorNormalizado =
          isPercentual && this.isValorFracionario(valor) ? valor * 100 : valor;
        resultado[chave] = this.formatarValorParaGrafico(valorNormalizado);
        return resultado;
      }, {});
    },
    normalizarValorReferencia(valorReferencia, isPercentual) {
      if (valorReferencia == null) {
        return null;
      }

      const referencia = Number(valorReferencia);
      if (!Number.isFinite(referencia)) {
        return null;
      }

      if (!isPercentual) {
        return referencia;
      }

      return this.isValorFracionario(referencia)
        ? referencia * 100
        : referencia;
    },
    formatarValorParaGrafico(valor) {
      const numero = Number(valor);
      if (!Number.isFinite(numero)) {
        return 0;
      }

      return Number(numero.toFixed(4));
    },
    isValorFracionario(valor) {
      const numero = Number(valor);
      if (!Number.isFinite(numero)) {
        return false;
      }

      return Math.abs(numero) > 0 && Math.abs(numero) <= 1;
    },
    getIncidenciaSuspensoesIndevidasVisaoMensal() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      return DashboardReligacaoService.getIncidenciaSuspensoesIndevidasVisaoMensal(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual
      )
        .then((res) => {
          this.dadosIncidenciaSuspensoesIndevidas = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getQntdTotalSuspensoesIndevidasVisaoMensal() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      return DashboardReligacaoService.getQntdTotalSuspensoesIndevidasVisaoMensal(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual
      )
        .then((res) => {
          this.dadosQntdTotalSuspensoesIndevidas = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getPercentualServicosReligacaoVisaoMensal() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      const servicos = this.servicosReligacaoSelecionados.join(",");
      return DashboardReligacaoService.getPercentualServicosReligacaoVisaoMensal(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual,
        servicos
      )
        .then((res) => {
          this.dadosPercentualServicosReligacao = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getQntdServicosDescumpridosVisaoMensal() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      const servicos = this.servicosReligacaoSelecionados.join(",");
      return DashboardReligacaoService.getQntdServicosDescumpridosVisaoMensal(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual,
        servicos
      )
        .then((res) => {
          this.dadosQntdServicosDescumpridos = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getIncidenciaSuspensoesIndevidasVisaoMensalAcumulados() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      return DashboardReligacaoService.getIncidenciaSuspensoesIndevidasVisaoMensalAcumulados(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual
      )
        .then((res) => {
          this.dadosIncidenciaSuspensoesIndevidasAcumulados = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getQtndTotalSuspensoesIndevidasVisaoMensalAcumulados() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      return DashboardReligacaoService.getQtndTotalSuspensoesIndevidasVisaoMensalAcumulados(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual
      )
        .then((res) => {
          this.dadosQtndTotalSuspensoesIndevidasAcumulados = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getPercentualServicosReligacaoVisaoMensalAcumulados() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      const servicos = this.servicosReligacaoSelecionados.join(",");
      return DashboardReligacaoService.getPercentualServicosReligacaoVisaoMensalAcumulados(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual,
        servicos
      )
        .then((res) => {
          this.dadosPercentualServicosReligacaoAcumulados = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getQntdServicosDescumpridosVisaoMensalAcumulados() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      const servicos = this.servicosReligacaoSelecionados.join(",");
      return DashboardReligacaoService.getQntdServicosDescumpridosVisaoMensalAcumulados(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual,
        servicos
      )
        .then((res) => {
          this.dadosQntdServicosDescumpridosAcumulados = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getQntdServicosRealizadosVisaoMensal() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      const servicos = this.servicosReligacaoSelecionados.join(",");
      return DashboardReligacaoService.getQntdServicosRealizadosVisaoMensal(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual,
        servicos
      )
        .then((res) => {
          this.dadosQntdServicosRealizados = res;
        })
        .catch((err) => {
          throw err;
        });
    },
    getQntdServicosRealizadosVisaoMensalAcumuladas() {
      const anoAnterior = dayjs(this.competencia)
        .subtract(1, "year")
        .format("YYYY-01");
      const anoAtual = dayjs(this.competencia).format("YYYY-12");
      const servicos = this.servicosReligacaoSelecionados.join(",");
      return DashboardReligacaoService.getQntdServicosRealizadosVisaoMensalAcumuladas(
        this.regionalSelecionada,
        anoAnterior,
        anoAtual,
        servicos
      )
        .then((res) => {
          this.dadosQntdServicosRealizadosAcumulados = res;
        })
        .catch((err) => {
          throw err;
        });
    },
  },
};
</script>
